/**
 * Studio (CMS) API client.
 *
 * Course and library authoring lives ONLY on Studio — the LMS 404s every one of these
 * paths. The dev server proxies them under the tenant host, which does two things at
 * once: the browser stays same-origin, and Studio resolves the tenant's Site so
 * `course_org_filter` and org permissions apply.
 *
 * Auth needs no separate Studio login. Signing into the LMS sets a JWT cookie scoped to
 * the shared parent domain, and Studio's DRF stack accepts it — verified returning 200
 * on all three endpoints below with only an LMS session.
 *
 * Route names read from `cms/djangoapps/contentstore/rest_api/v1/urls.py` and
 * `content_libraries/rest_api` at release/verawood.1.
 *
 * NO LEGACY ENDPOINTS. Everything here is a versioned REST API — contentstore v1,
 * libraries v2, or our own v1 authoring view. Studio's legacy handlers are session-only
 * and cannot be reached from a headless app anyway, so there is nothing to fall back to.
 */

import { openedx } from './openedx';

/* ---------------------------------------------------------------- home context ---- */

export interface StudioHome {
  /**
   * Orgs this user may create in.
   *
   * EMPTY MEANS UNRESTRICTED, not "none" — a superuser gets `[]`. Treating empty as
   * "no permission" would lock out exactly the people who have the most.
   */
  allowed_organizations: string[];
  allowed_organizations_for_libraries: string[];
  can_create_organizations: boolean;
  allow_course_reruns: boolean;
  libraries_v2_enabled: boolean;
  can_access_advanced_settings: boolean;
  /**
   * Global course-creator status: 'unrequested' | 'pending' | 'granted' | 'denied', or null.
   *
   * Reported for completeness, but DO NOT gate our create buttons on it. It answers the
   * platform's question ("may this person create libraries anywhere on the install?"), which is
   * not ours. Library creation goes through our tenant endpoint, which asks an org-scoped
   * question instead — so a tenant admin with no global status can create libraries in their own
   * academy and nowhere else. Gating on this hid the button from exactly the people entitled to
   * use it.
   */
  course_creator_status: string | null;
  /**
   * Courses currently being created or rerun — the SAME list `/home/courses` returns, from
   * the same `UnsucceededCourseSerializer`, verified field for field against the running CMS.
   *
   * IT IS HERE, AND THAT IS WHY `/admin/courses` NEEDS NO COURSE CALL TO STUDIO AT ALL. The
   * pending rows were the one thing on that page which genuinely had to come from Studio —
   * the course rows themselves now come from the tenant-scoped academy endpoint. Measured,
   * this response is 898 bytes and the course listing it replaces was 63.6 KB per org.
   *
   * Optional because it is `allow_null` upstream.
   */
  in_process_course_actions?: InProcessCourse[] | null;
}

export const studioHome = () => openedx.get<StudioHome>('/api/contentstore/v1/home');

/* --------------------------------------------------------------------- courses ---- */

export interface StudioCourse {
  course_key: string;
  display_name: string;
  org: string;
  number: string;
  run: string;
  /** Studio outline URL, relative to the Studio host. */
  url: string;
  lms_link: string;
  rerun_link: string;
}

/** A course currently being created or rerun — Studio does this asynchronously. */
export interface InProcessCourse {
  display_name: string;
  org: string;
  number: string;
  run: string;
  is_failed: boolean;
  is_in_progress: boolean;
  dismiss_link: string;
}

/**
 * A page of courses.
 *
 * v2 splits active and archived into separate REQUESTS rather than returning both lists in one
 * response, which is what makes pagination possible: v1 hands back every course a user can see
 * in a single unpaginated payload, so a tenant with several hundred courses pays for all of them
 * on every page load.
 */
export interface CoursePage {
  courses: StudioCourse[];
  in_process_course_actions: InProcessCourse[];
  /** Total matching this filter, not the length of this page — used for the tab badges. */
  count: number;
  num_pages: number;
}

/** Envelope v2 wraps its payload in. v1 had no envelope at all. */
interface CoursesV2 {
  count: number;
  num_pages: number;
  results: {
    courses?: StudioCourse[] | null;
    in_process_course_actions?: InProcessCourse[] | null;
  };
}

/**
 * List courses — contentstore **v2**.
 *
 * v1 (`/api/contentstore/v1/home/courses`) still works and returns `courses`,
 * `archived_courses` and `in_process_course_actions` together. v2 is the current endpoint and
 * adds `page`, `page_size`, `order` and the `active_only` / `archived_only` filters. The cost of
 * moving is that archived is now a second request; the gain is that the request size stops
 * growing with the catalogue.
 */
export async function listCourses(
  params: {
    org?: string;
    search?: string;
    archived?: boolean;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<CoursePage> {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  // Exactly one of these, never both — sending both returns an empty set.
  query.set(params.archived ? 'archived_only' : 'active_only', 'true');

  /*
   * `org` IS NOT SENT, AND THE ENDPOINT IS WHY. It documents the parameter — the view's own
   * OpenAPI annotation says "Query param to filter by course org" and its docstring shows
   * `?org=edX` — and then ignores it: `get_course_context_v2` calls
   * `get_courses_accessible_to_user(request)` and never reads it. Measured on this install,
   * every one of these returned the same 105 courses:
   *
   *     ?org=MEREKA      count 105, first rows from another org
   *     ?org=<other>     count 105, identical
   *     (no org)         count 105
   *
   * while `search`, `order`, `active_only` and `archived_only` all work properly (`?search=…`
   * narrowed 105 to 4). Trusting the documented parameter is what put one academy's courses in
   * another academy's admin listing, labelled as licensed content — a tenancy leak wearing a
   * badge. So the filter is applied HERE, where every caller inherits it.
   *
   * That forces the page size too: server pagination would slice BEFORE this filter, handing
   * back a page that is mostly other orgs. When an org is named, the whole catalogue is fetched
   * and paginated by the caller instead, and `count` reports what the filter left rather than
   * what the server counted.
   */
  const filtering = Boolean(params.org);
  query.set('page', String(filtering ? 1 : (params.page ?? 1)));
  query.set('page_size', String(filtering ? 500 : (params.pageSize ?? 24)));

  const data = await openedx.get<CoursesV2>(`/api/contentstore/v2/home/courses?${query}`);
  const returned = data.results?.courses ?? [];
  const courses = params.org
    ? returned.filter((course) => course.org.toUpperCase() === params.org!.toUpperCase())
    : returned;

  return {
    courses,
    in_process_course_actions: data.results?.in_process_course_actions ?? [],
    count: params.org ? courses.length : (data.count ?? 0),
    num_pages: params.org ? 1 : (data.num_pages ?? 1),
  };
}

export interface NewCourse {
  org: string;
  number: string;
  run: string;
  display_name: string;
}

/**
 * Create a course.
 *
 * Calls OUR endpoint in the CMS, not Studio's legacy `/course/`. That view is
 * `@login_required` and authenticates from `studio_session_id`, a cookie scoped to the
 * Studio hostname that can never reach an app served from the tenant host. Ours is a DRF
 * view, so the JWT cookie already in use authenticates it — one auth mechanism for the
 * whole app, and no second sign-in.
 *
 * The org is NOT sent: the server takes it from the tenant. A client that could name its
 * own org could file courses into another academy.
 */
export async function createCourse(
  slug: string,
  course: { title: string; number?: string; run: string },
): Promise<{ course_key: string; url: string }> {
  return openedx.post<{ course_key: string; url: string }>(
    `/api/authoring/v1/tenants/${slug}/courses`,
    course,
  );
}

/**
 * Absolute Studio origin, for links that must be top-level navigations.
 *
 * Editing a course opens Studio's own pages, which need the Studio session cookie. That
 * cookie is host-scoped, so those links cannot go through our proxy — they have to
 * target Studio directly. Per-environment, never per-tenant.
 */
export function studioOrigin(tenantStudioDomain: string | null): string {
  if (tenantStudioDomain) return `${window.location.protocol}//${tenantStudioDomain}`;
  return import.meta.env.VITE_STUDIO_ORIGIN ?? '';
}

/** Studio URLs from the listing are relative; make them absolute for navigation. */
export function studioUrl(path: string, tenantStudioDomain: string | null): string {
  if (path.startsWith('http')) return path;
  return `${studioOrigin(tenantStudioDomain)}${path}`;
}

/* ------------------------------------------------------------------- libraries ---- */

export interface StudioLibrary {
  id: string;
  org: string;
  slug: string;
  title: string;
  description: string;
  num_blocks: number;
  has_unpublished_changes: boolean;
  has_unpublished_deletes: boolean;
  last_published: string | null;
  published_by: string | null;
  can_edit_library: boolean;
  license: string;
  allow_public_read: boolean;
  allow_public_learning: boolean;
  updated: string;
}

/**
 * Libraries in this academy.
 *
 * OURS, not `/api/libraries/v2/`. The platform's listing filters by
 * `CAN_VIEW_THIS_CONTENT_LIBRARY`, which is an explicit PER-LIBRARY grant with no notion of an
 * organisation. Measured: a user holding both OrgStaffRole and OrgInstructorRole for the owning
 * org, but no explicit grant, sees NONE of that org's libraries. In practice an academy admin
 * sees only the libraries they personally created — which reads as "libraries are broken".
 *
 * Ours returns every library belonging to the academy's organisations for anyone with an org
 * role, and falls back to the platform's own per-library filter for everyone else — which is
 * what lets a member be given one library and see exactly that one.
 */
export async function listLibraries(
  tenantSlug: string,
  params: { search?: string; order?: string } = {},
): Promise<StudioLibrary[]> {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.order) query.set('order', params.order);
  const suffix = query.toString() ? `?${query}` : '';
  return openedx.get<StudioLibrary[]>(
    `/api/authoring/v1/tenants/${tenantSlug}/libraries${suffix}`,
  );
}

export interface NewLibrary {
  /**
   * Optional, and normally omitted: the server takes the org from the tenant. Send it only when a
   * tenant owns several orgs and the author has genuinely chosen one — it is validated against
   * that tenant's list, never trusted.
   */
  org?: string;
  slug: string;
  title: string;
  description?: string;
  license?: string;
}

/**
 * Create a library inside this tenant.
 *
 * Calls OUR endpoint, not `POST /api/libraries/v2/`, for the same reason course creation does.
 * The platform's endpoint gates on a GLOBAL permission — `is_global_staff | is_course_creator` —
 * with no notion of a tenant, and the `CourseCreator` record that satisfies it defaults to
 * `all_organizations = True`. Granting it to a tenant admin therefore lets them create libraries
 * under every organisation on the install — measured on a real install, not assumed: an admin
 * belonging to one academy came back able to create under a second, unrelated one.
 *
 * Ours takes the org from the tenant and checks org-scoped roles, so the boundary holds without
 * anyone needing platform-wide rights.
 */
export const createLibrary = (slug: string, library: NewLibrary) =>
  openedx.post<{ id: string; org: string; slug: string; title: string }>(
    `/api/authoring/v1/tenants/${slug}/libraries`,
    library,
  );

/* ----------------------------------------------------------------------- keys ------ */

/**
 * Derive the course number from a title.
 *
 * Studio builds the course key from org+number+run, and asking an author for a "course
 * number" is asking them to invent a database identifier. This derives it, and the
 * caller shows the resulting key so it is never a surprise later in URLs and reports.
 */
export function deriveNumber(title: string): string {
  const base = title
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '')
    .slice(0, 20);
  return base || 'COURSE';
}

/** Slug for a library key: lowercase, hyphenated, safe for `lib:ORG:slug`. */
export function deriveSlug(title: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return base || 'library';
}

/** Current year as the run. Reruns increment it; that is what course_rerun is for. */
export function defaultRun(now: Date): string {
  return String(now.getFullYear());
}

export function courseKeyFor(org: string, number: string, run: string): string {
  return `course-v1:${org}+${number}+${run}`;
}

/**
 * Which org to file new content under.
 *
 * `allowed_organizations` is authoritative when non-empty. When empty — a superuser —
 * fall back to the tenant's own orgs, so content still lands in the academy the admin
 * is currently looking at rather than wherever Studio would default.
 */
export function resolveOrgs(allowed: string[], tenantOrgs: string[]): string[] {
  return allowed.length > 0 ? allowed : tenantOrgs;
}

/* --------------------------------------------------------------------- outline ---- */

/**
 * What a block permits, computed server-side.
 *
 * Read these rather than inferring from category. Whether a block can be deleted or
 * dragged depends on things the client cannot see — a library-linked block, a released
 * exam, a split-test child. Studio's own outline gates its controls on exactly this
 * object, so honouring it is how we stay consistent with the platform instead of
 * offering an action the server will then refuse.
 */
export interface BlockActions {
  deletable: boolean;
  draggable: boolean;
  childAddable: boolean;
  duplicable: boolean;
  /** Non-null only for blocks linked to a v2 library. */
  unlinkable: boolean | null;
}

/**
 * Where a block sits between "author is still writing" and "a learner can see it".
 *
 * Two independent axes are folded into this one value, which is why it has more states
 * than a boolean: published-or-draft, and released-or-scheduled. `needs_attention` is
 * the important one — it means the descendants disagree with each other, and it is the
 * only state an author has to act on.
 */
export type VisibilityState =
  | 'live'
  | 'ready'
  | 'unscheduled'
  | 'needs_attention'
  | 'staff_only'
  | 'gated'
  | 'hide_from_toc';

/**
 * `child_info` carries the type of child to add and what to call it.
 *
 * This is why the outline never hardcodes chapter/sequential/vertical or the words
 * "Section"/"Subsection"/"Unit": the server names them, they are translated, and an
 * install can change them.
 */
export interface ChildInfo {
  category: string;
  display_name: string;
  children: OutlineBlock[];
}

/**
 * A node in the outline.
 *
 * Field names are Studio's `create_xblock_info` output verbatim, read from the live
 * response rather than the serializer — `course_structure` is a bare `DictField`, so the
 * serializer declares nothing about its shape.
 *
 * Note the two date representations: `start`/`due` are ISO and are what you send back,
 * while `release_date`/`due_date` are pre-formatted for display in the user's locale.
 * Editing the formatted one would round-trip a localised string into a date field.
 */
export interface OutlineBlock {
  id: string;
  display_name: string;
  category: string;
  /** 'problem' | 'video' | 'other' — drives the icon at unit level. */
  xblock_type: string;
  has_children: boolean;
  published: boolean;
  has_changes: boolean;
  visibility_state: VisibilityState | null;
  /** This block's own staff lock, as opposed to one inherited from an ancestor. */
  has_explicit_staff_lock: boolean;
  ancestor_has_staff_lock: boolean;
  staff_only_message: boolean;
  hide_from_toc: boolean;
  enable_hide_from_toc_ui: boolean;
  /** ISO, for writing back. Null means unscheduled. */
  start: string | null;
  due: string | null;
  /** Pre-formatted, for display. */
  release_date: string | null;
  due_date: string;
  released_to_students: boolean;
  graded: boolean;
  /** The assignment type, e.g. "Homework". Null when ungraded. */
  format: string | null;
  /** Grader types available in this course — the options for `format`. */
  course_graders: string[];
  edited_on: string;
  published_on: string | null;
  lms_url: string;
  actions: BlockActions;
  /** Set when a block came from a v2 library and may be out of sync. */
  upstream_info?: { upstream_ref: string | null; ready_to_sync: boolean } | null;
  explanatory_message: string | null;
  child_info?: ChildInfo | null;
}

export interface CourseOutline {
  course_structure: OutlineBlock;
  lms_link: string;
  course_release_date: string | null;
}

/**
 * Read the outline.
 *
 * `course_index` is DRF, so the JWT authenticates it and no server-side wrapper is
 * needed for reads — only writes go through our own app.
 */
export const getOutline = (courseKey: string) =>
  openedx.get<CourseOutline>(`/api/contentstore/v1/course_index/${encodeURIComponent(courseKey)}`);

/**
 * Block operations, all through the one function Studio itself uses.
 *
 * Writes go to OUR CMS app because Studio's `xblock_handler` is a session-authenticated
 * legacy view and its cookie is scoped to the Studio hostname — unreachable from an app
 * served on the tenant host. The wrapper calls the same `handle_xblock` service method,
 * so the payload shapes below are Studio's own, unchanged, and its permission checks
 * still run.
 */
const XBLOCK = '/api/authoring/v1/xblock';

export const addBlock = (parentLocator: string, category: string, displayName?: string) =>
  openedx.post<{ locator: string }>(XBLOCK, {
    parent_locator: parentLocator,
    category,
    ...(displayName ? { display_name: displayName } : {}),
  });

export const renameBlock = (locator: string, displayName: string) =>
  openedx.patch(`${XBLOCK}/${locator}`, { metadata: { display_name: displayName } });

export const publishBlock = (locator: string) =>
  openedx.patch(`${XBLOCK}/${locator}`, { publish: 'make_public' });

export const discardChanges = (locator: string) =>
  openedx.patch(`${XBLOCK}/${locator}`, { publish: 'discard_changes' });

/** Reorder by sending the parent's children in the new order — Studio's own contract. */
export const reorderChildren = (parentLocator: string, childLocators: string[]) =>
  openedx.patch(`${XBLOCK}/${parentLocator}`, { children: childLocators });

export const deleteBlock = (locator: string) => openedx.delete<void>(`${XBLOCK}/${locator}`);

export const duplicateBlock = (parentLocator: string, sourceLocator: string) =>
  openedx.post<{ locator: string }>(XBLOCK, {
    parent_locator: parentLocator,
    duplicate_source_locator: sourceLocator,
  });

/**
 * Visibility, schedule and grading — every field the outline can change on a block.
 *
 * One call rather than four, because they are edited together in one dialog and four
 * sequential PATCHes would leave a half-applied block if the third failed.
 *
 * `null` is meaningful and is NOT the same as omitting the key: sending `start: null`
 * unschedules a block, whereas omitting it leaves the existing date alone. So callers
 * pass exactly the fields they mean to change.
 */
export interface BlockSettings {
  start?: string | null;
  due?: string | null;
  visible_to_staff_only?: boolean | null;
  hide_from_toc?: boolean;
  graded?: boolean;
  format?: string | null;
}

export const updateBlockSettings = (locator: string, settings: BlockSettings) =>
  openedx.patch(`${XBLOCK}/${locator}`, { metadata: settings });

/**
 * Move a block to a different parent.
 *
 * Deliberately NOT the same call as `reorderChildren`. Studio dispatches on whether the
 * URL carries a usage key: with one, a PATCH updates that block; without one, a PATCH
 * carrying `move_source_locator` is a move. So this posts to the bare path — adding the
 * locator to the URL would silently turn it into an update and drop the move.
 *
 * `target_index` past the end is rejected by the server rather than clamped, so the
 * caller passes an index within the destination's current children.
 */
export const moveBlock = (
  sourceLocator: string,
  targetParentLocator: string,
  targetIndex?: number,
) =>
  openedx.patch(XBLOCK, {
    move_source_locator: sourceLocator,
    parent_locator: targetParentLocator,
    ...(targetIndex === undefined ? {} : { target_index: targetIndex }),
  });
